/**
 * openBankingSpec — PSD2 open-banking capability (white-label
 * `open_banking`; the BE ConnectorFactory resolves the per-country backend
 * driver — vendor names stay BE-side per CLAUDE.md memory rule).
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/OpenBankingHubPage.tsx (265)
 *
 * Flow:
 *   1. country picker (drives the BE backend-routing)
 *   2. bank picker (BE-driven list of supported ASPSPs)
 *   3. consent (PSD2 mandates user redirect to the bank's portal)
 *
 * The custom `body` slot on the `bank` step renders a placeholder list —
 * the BE bank-search endpoint will replace it once ported.
 */

import type { BaseConnection, CapabilityWizardSpec, StepRenderCtx } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Landmark, Shield, TrendingUp, Wallet } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import { useInstitutions } from "../hooks/useInstitutions";

export interface OpenBankingConnection extends BaseConnection {
  readonly institution_id?: string;
  readonly institution_name?: string;
  readonly country?: string;
  readonly consent_expires_at?: string | null;
}

const iconCls = "h-4 w-4";

function BankPickerBody(ctx: StepRenderCtx<OpenBankingConnection>) {
  // BE endpoint: GET /connectors/open_banking/institutions. We default the
  // country to whatever the previous step persisted; if it's missing
  // (deep-link, first run) we fall back to "IT".
  const country = (ctx.state.country as string | undefined) ?? "IT";
  const query = useInstitutions(country);

  if (query.isLoading) {
    return (
      <div className="rounded-md border border-hairline bg-card/60 p-4 text-ink-3 text-sm">
        <p>
          <Trans>Caricamento elenco banche {country}…</Trans>
        </p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="rounded-md border border-hairline bg-rose-50/60 p-4 text-rose-900 text-sm">
        <p>
          <Trans>
            Impossibile recuperare le banche disponibili. Riprova fra qualche secondo.
          </Trans>
        </p>
      </div>
    );
  }

  const institutions = query.data ?? [];
  if (institutions.length === 0) {
    // BE adapter returns [] when the provider requires a user-scoped
    // token (hosted-picker flow). The "Procedi al consenso" step below
    // will then call the hosted picker on the provider portal with an
    // empty institution_id — the bank is chosen there.
    return (
      <div className="rounded-md border border-hairline bg-card/60 p-4 text-ink-3 text-sm">
        <p>
          <Trans>
            Per il tuo paese la selezione della banca avviene direttamente sul portale
            dell'aggregatore PSD2.
          </Trans>
        </p>
        <p className="mt-1.5">
          <Trans>
            Premi <span className="font-medium">"Procedi al consenso"</span> per aprire il
            selettore ufficiale e completare il flusso PSD2 (consenso valido 90 giorni).
          </Trans>
        </p>
      </div>
    );
  }

  const selectedId = ctx.state.institution_id as string | undefined;
  return (
    <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
      {institutions.map((inst) => {
        const isSelected = selectedId === inst.id;
        return (
          <li key={inst.id}>
            <button
              type="button"
              onClick={() => {
                ctx.patch({
                  institution_id: inst.id,
                  institution_name: inst.name,
                });
              }}
              className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                isSelected
                  ? "border-accent-9 bg-accent-2/40 text-ink-1"
                  : "border-hairline bg-card/60 text-ink-1 hover:bg-card/80"
              }`}
            >
              {inst.logo_url ? (
                <img
                  src={inst.logo_url}
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-6 flex-shrink-0 rounded-sm object-contain"
                />
              ) : (
                <Landmark className="h-6 w-6 flex-shrink-0 text-ink-3" aria-hidden />
              )}
              <span className="flex flex-col">
                <span className="font-medium">{inst.name}</span>
                {inst.bic ? (
                  <span className="font-mono text-ink-3 text-xs">{inst.bic}</span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export const openBankingSpec: CapabilityWizardSpec<OpenBankingConnection> = {
  capability: "open_banking",
  title: msg`Conti bancari`,
  subtitle: msg`Saldi, movimenti, riconciliazione — via PSD2 (consenso 90 giorni)`,
  icon: <Landmark className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 60,

  vendors: [
    {
      id: "open_banking",
      label: "Open Banking PSD2",
      icon: <Landmark className={iconCls} aria-hidden />,
      whiteLabel: true,
      description: msg`Routing automatico in base al paese — gestito da Ando.`,
    },
  ],

  steps: [
    {
      id: "country",
      kind: "form",
      title: msg`Paese`,
      description: msg`Seleziona il paese della tua banca.`,
      fields: [
        {
          name: "country",
          label: msg`Paese`,
          type: "select",
          required: true,
          options: [
            { value: "IT", label: msg`Italia` },
            { value: "DE", label: msg`Germania` },
            { value: "FR", label: msg`Francia` },
            { value: "ES", label: msg`Spagna` },
            { value: "NL", label: msg`Paesi Bassi` },
          ],
        },
      ],
    },
    {
      id: "bank",
      kind: "select",
      title: msg`Banca`,
      description: msg`Scegli la tua banca dall'elenco PSD2 supportato.`,
      body: (ctx) => BankPickerBody(ctx),
    },
    {
      id: "consent",
      kind: "oauth",
      title: msg`Consenso PSD2`,
      description: msg`Verrai reindirizzato al portale della tua banca per il consenso.`,
      oauth: { buttonLabel: msg`Procedi al consenso` },
    },
  ],

  benefits: [
    {
      icon: <Wallet className={iconCls} aria-hidden />,
      title: msg`Saldi in tempo reale`,
      body: msg`Cash position aggiornata su tutti i conti collegati.`,
    },
    {
      icon: <TrendingUp className={iconCls} aria-hidden />,
      title: msg`Cash flow forecast`,
      body: msg`Ando proietta entrate e uscite usando lo storico.`,
    },
    {
      icon: <Landmark className={iconCls} aria-hidden />,
      title: msg`Riconciliazione automatica`,
      body: msg`Movimenti ↔ fatture, suggerimenti pronti per la conferma.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: "PSD2 + GDPR",
      body: msg`Solo accesso in lettura, consenso revocabile in ogni momento.`,
    },
  ],

  api: createCapabilityApi<OpenBankingConnection>({
    capability: "open_banking",
    defaultVendor: "open_banking",
  }),
  connectionKeyField: "id" as keyof OpenBankingConnection,
  getDisplayName: (c) =>
    c.institution_name ?? c.institution_id ?? c.id ?? i18nSingleton._(msg`Banca`),
  getSecondaryId: (c) =>
    c.country
      ? `${c.country} · ${i18nSingleton._(msg`consenso`)} ${c.consent_expires_at ?? i18nSingleton._(msg`n/d`)}`
      : null,
};
